import { ButtonTertiary, IconButton } from "@nova/component-btn"
import * as Icons from "@nova/component-icons"
import { Card } from "features/pilot-tracking/MetricLayout"
import { Status } from "@nova/ds-component-status"
import { zIndex } from "@nova/constants"
import { ChecklistKeyword, ChecklistPdf } from "@nova/schemas"
import classNames from "classnames"
import { useRef, useState } from "react"
import { createPortal } from "react-dom"
import { ExpandableContent } from "@nova/component-expandable-content"
import { useAppDispatch, useAppSelector } from "integrations/redux/hooks"
import {
    setChecklistCreationTitle,
    setChecklistCreationDescription,
    clearChecklistCreationQuestions,
    closeChecklistCreationModal,
    setGeneratingAiChecklist,
    setChecklistCreationQuestions,
    selectGeneratingAiChecklist,
} from "features/pilot-tracking/checklists/slice"
import { GlobalNotificationStatus } from "@nova/component-global-notification"
import { setGlobalNotificationContext } from "user/profile/slice"
import { useRouter } from "pages"
import { useGenerateAiChecklist } from "features/pilot-tracking/checklists/ChecklistQueries"
import { useLoadingText } from "features/data-management/workspace/workspace/useLoadingText"
import { GradientBackground } from "./GradientBackground"

export interface AIChecklistContext {
    path: "ai"
    prompt: string
    keywords: ChecklistKeyword[]
    pdf: ChecklistPdf | null
}

export interface ManualChecklistContext {
    path: "manual"
}
export type ChecklistBuilderContext = AIChecklistContext | ManualChecklistContext

export const CreateChecklistModal = ({ onClose }: { onClose: () => void }) => {
    const dispatch = useAppDispatch()
    const router = useRouter("/workspace/:workspaceId")
    const [showAiForm, setShowAiForm] = useState(false)
    const [aiPrompt, setAiPrompt] = useState("")
    const [aiKeywords, setAiKeywords] = useState<ChecklistKeyword[]>([])
    const { generateChecklist } = useGenerateAiChecklist()
    const generatingChecklist = useAppSelector(selectGeneratingAiChecklist)
    const generatingText = useLoadingText(generatingChecklist, "Generating Checklist")

    const handleSubmit = async (context: ChecklistBuilderContext) => {
        dispatch(setChecklistCreationTitle(null))
        dispatch(setChecklistCreationDescription(null))
        dispatch(clearChecklistCreationQuestions())
        if (context.path === "manual") {
            dispatch(closeChecklistCreationModal())
            router.navigate({ path: "/fleet/checklists/create" })
            return
        }
        dispatch(setGeneratingAiChecklist(true))
        try {
            const result = await generateChecklist({
                prompt: context.prompt,
                keywords: context.keywords,
                pdf: context.pdf ?? undefined,
            })
            if (!result?.length) {
                dispatch(
                    setGlobalNotificationContext({
                        message: "Failed to create a checklist. Try changing your description.",
                        status: GlobalNotificationStatus.error,
                        timeoutInMs: 6000,
                    })
                )
                return
            }
            dispatch(setChecklistCreationQuestions(result))
            dispatch(closeChecklistCreationModal())
            router.navigate({ path: "/fleet/checklists/create" })
        } catch {
            dispatch(
                setGlobalNotificationContext({
                    message: "Failed to create a checklist. Try changing your description.",
                    status: GlobalNotificationStatus.error,
                    timeoutInMs: 6000,
                })
            )
        } finally {
            dispatch(setGeneratingAiChecklist(false))
        }
    }

    return createPortal(
        <div
            className="fixed left-0 top-0 w-full h-full bg-black-800/80 backdrop-blur-md flex flex-col p-8 pt-6"
            style={{ zIndex: zIndex.HighPriorityModal + 1 }}
        >
            <div className="flex items-center justify-between w-full">
                {generatingChecklist ? (
                    <>
                        <div />
                        <BuildWithAiTag />
                        <div />
                    </>
                ) : showAiForm ? (
                    <>
                        <div className="min-w-[90px]">
                            <IconButton
                                className="text-gray-100 flex items-center gap-2"
                                onClick={() => setShowAiForm(false)}
                            >
                                <div className="w-9 aspect-square flex justify-center items-center rounded-full bg-white-100">
                                    <Icons.SmallIcon
                                        Icon={Icons.WorkspaceArrow}
                                        className="rotate-180"
                                    />
                                </div>
                                <small className="xs">Back</small>
                            </IconButton>
                        </div>
                        <BuildWithAiTag />
                        <div className="min-w-[90px]" />
                    </>
                ) : (
                    <IconButton
                        className="text-gray-100 flex items-center gap-2 transition-colors"
                        onClick={onClose}
                    >
                        <div className="w-9 aspect-square flex justify-center items-center rounded-full bg-white-100">
                            <Icons.Icon Icon={Icons.XClose} />
                        </div>
                        <small className="xs">Cancel</small>
                    </IconButton>
                )}
            </div>
            <div className="flex flex-col flex-1 items-center justify-center gap-6 relative">
                {generatingChecklist ? (
                    <>
                        <style>{`
                                    @keyframes blob-spin {
                                        from { transform: translate(-50%, -50%) rotate(0deg); }
                                        to { transform: translate(-50%, -50%) rotate(360deg); }
                                    }
                                    @keyframes blob-spin-reverse {
                                        from { transform: translate(-50%, -50%) rotate(0deg); }
                                        to { transform: translate(-50%, -50%) rotate(-360deg); }
                                    }
                                `}</style>
                        <div
                            className="absolute"
                            style={{
                                width: 299,
                                height: 170,
                                borderRadius: 133,
                                background: "#F1FF66",
                                top: "50%",
                                left: "50%",
                                filter: "blur(34px)",
                                opacity: 0.15,
                                animation: "blob-spin 9s linear infinite",
                            }}
                        />
                        <div
                            className="absolute"
                            style={{
                                width: 220,
                                height: 120,
                                borderRadius: 110,
                                background: "#F1FF66",
                                top: "50%",
                                left: "50%",
                                filter: "blur(50px)",
                                opacity: 0.1,
                                animation: "blob-spin-reverse 6s linear infinite",
                            }}
                        />
                        <small className="xs text-white">{generatingText}</small>
                    </>
                ) : showAiForm ? (
                    <CreateAiChecklistForm
                        prompt={aiPrompt}
                        onPromptChange={setAiPrompt}
                        selectedKeywords={aiKeywords}
                        onKeywordsChange={setAiKeywords}
                        onSubmit={handleSubmit}
                    />
                ) : (
                    <>
                        <div className="flex flex-col items-center gap-2">
                            <small className="xs text-white-500">New Checklist</small>
                            <h2 className="text-[28px] text-white">How would you like to start?</h2>
                        </div>
                        <div className="flex gap-6 min-h-[300px]">
                            <GradientBackground
                                onlyWhenHovered
                                className="hover:shadow-xl hover:shadow-white-200 group/ai h-full rounded-xl"
                            >
                                <div
                                    className="bg-black-1000 rounded-[10px] p-6 flex flex-col gap-4 w-80 h-full group-hover/ai:bg-gray-700/100 border border-white-100 group-hover/ai:border-black-1000 cursor-pointer"
                                    onClick={() => setShowAiForm(true)}
                                >
                                    <div className="relative">
                                        <Icons.LargeIcon
                                            Icon={Icons.ModelTraining}
                                            className="transition-opacity group-hover/ai:opacity-0 text-white-400"
                                        />
                                        <Icons.LargeIcon
                                            Icon={Icons.ModelTrainingGradient}
                                            className="absolute inset-0 opacity-0 transition-opacity group-hover/ai:opacity-100"
                                        />
                                    </div>
                                    <div className="grow" />
                                    <div className="flex flex-col gap-2">
                                        <h3 className="text-white">Build with AI</h3>
                                        <h5 className="text-gray-200">
                                            Create questions for your checklist from a short
                                            description of your operation.
                                        </h5>
                                    </div>
                                    <div className="w-fit">
                                        <ButtonTertiary
                                            title="Start with AI"
                                            RightIcon={Icons.ArrowRight}
                                        />
                                    </div>
                                </div>
                            </GradientBackground>

                            <Card
                                className="group/manual p-6 flex flex-col gap-4 w-80 h-full hover:ring-2 hover:ring-blue-500 hover:bg-gray-700/100  hover:border-transparent transition-shadow cursor-pointer"
                                onClick={() => handleSubmit({ path: "manual" })}
                            >
                                <div className="text-white-400 group-hover/manual:!text-white-1000">
                                    <Icons.LargeIcon Icon={Icons.NoteAdd} />
                                </div>
                                <div className="grow" />
                                <div className="flex flex-col gap-2">
                                    <h3 className="text-white">Start from Scratch</h3>
                                    <h5 className="text-gray-200">
                                        Begin with an empty checklist and add questions manually.
                                    </h5>
                                </div>
                                <div className="w-fit">
                                    <ButtonTertiary
                                        title="Create Checklist"
                                        RightIcon={Icons.ArrowRight}
                                    />
                                </div>
                            </Card>
                        </div>
                        <div className="h-20" />
                    </>
                )}
            </div>
        </div>,
        document.getElementById("portal")!
    )
}

const fileToChecklistPdf = (file: File): Promise<ChecklistPdf> =>
    new Promise((resolve, reject) => {
        const reader = new FileReader()
        reader.onload = () => {
            const result = reader.result
            if (typeof result !== "string") return reject(new Error("Failed to read PDF."))
            resolve({ filename: file.name, base64: result.split(",")[1] ?? "" })
        }
        reader.onerror = () => reject(reader.error ?? new Error("Failed to read PDF."))
        reader.readAsDataURL(file)
    })

const BuildWithAiTag = () => {
    return (
        <GradientBackground className="rounded-full !p-px">
            <div className="flex items-center gap-1 py-1 pl-3 pr-4 bg-black-1000 rounded-full text-white-500">
                <Icons.SmallIcon Icon={Icons.ModelTraining} className="pb-1" />
                <small className="xs">Build with AI</small>
            </div>
        </GradientBackground>
    )
}

const KEYWORD_CATEGORIES = [
    { label: "Regulation", options: ["Part 107", "COA", "BVLOS"] },
    {
        label: "Operation",
        options: ["Mapping", "Inspection", "Training", "Wildfire", "Search & Rescue"],
    },
    { label: "Environment", options: ["Urban", "Rural", "Mixed use", "Forested", "Industrial"] },
    {
        label: "Constraints",
        options: ["Night operations", "High winds", "Complex airspace", "Low visibility"],
    },
    { label: "Aircraft", options: ["Multirotor", "Fixed wing", "Thermal payload", "RTK"] },
]

const CreateAiChecklistForm = ({
    prompt,
    onPromptChange,
    selectedKeywords,
    onKeywordsChange,
    onSubmit,
}: {
    prompt: string
    onPromptChange: (prompt: string) => void
    selectedKeywords: ChecklistKeyword[]
    onKeywordsChange: (keywords: ChecklistKeyword[]) => void
    onSubmit: (context: AIChecklistContext) => void
}) => {
    const [keywordsOpen, setKeywordsOpen] = useState(false)
    const [openCategories, setOpenCategories] = useState<Set<string>>(new Set())
    const [uploadedFile, setUploadedFile] = useState<File | null>(null)
    const fileInputRef = useRef<HTMLInputElement>(null)

    const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0]
        if (!file) return
        setUploadedFile(file)
        e.target.value = ""
    }

    const handleGenerate = async () => {
        const pdf = uploadedFile ? await fileToChecklistPdf(uploadedFile) : null
        onSubmit({ path: "ai", prompt, keywords: selectedKeywords, pdf })
    }

    const toggleCategory = (label: string) => {
        setOpenCategories((prev) => {
            const next = new Set(prev)
            if (next.has(label)) next.delete(label)
            else next.add(label)
            return next
        })
    }

    const toggleKeyword = (type: string, value: string) => {
        onKeywordsChange(
            selectedKeywords.some((k) => k.type === type && k.value === value)
                ? selectedKeywords.filter((k) => !(k.type === type && k.value === value))
                : [...selectedKeywords, { type, value }]
        )
    }

    const selectAllInCategory = (label: string, options: string[]) => {
        const toAdd = options
            .filter((o) => !selectedKeywords.some((k) => k.type === label && k.value === o))
            .map((o) => ({ type: label, value: o }))
        onKeywordsChange([...selectedKeywords, ...toAdd])
    }

    const deselectAllInCategory = (label: string) => {
        onKeywordsChange(selectedKeywords.filter((k) => k.type !== label))
    }

    return (
        <>
            <div className="flex flex-col items-center gap-2">
                <small className="tracking-widest uppercase text-gray-300">New Checklist</small>
                <h2 className="text-[28px] text-white">Describe your checklist</h2>
            </div>
            <div className="w-[680px] rounded-xl overflow-hidden border border-gray-700">
                <textarea
                    className={classNames(
                        "transition-colors",
                        "bg-white-100 hover:bg-gray-700 active:bg-gray-700 focus:bg-gray-700",
                        "!shadow-none !border-none !outline-none",
                        "text-white placeholder-gray-400 p-4 w-full h-28 text-base resize-none"
                    )}
                    style={{ outline: "none" }}
                    placeholder="ie. Create a full pre-flight safety checklist focusing on safety, airspace, weather, crew coordination and aircraft readiness."
                    value={prompt}
                    onChange={(e) => onPromptChange(e.target.value)}
                />
                <div className="flex items-center justify-between px-3 py-4">
                    <div
                        className={classNames(
                            "flex items-center gap-3 cursor-pointer transition-colors",
                            {
                                "text-gray-100 hover:text-white": keywordsOpen,
                                "text-gray-300 hover:text-white": !keywordsOpen,
                            }
                        )}
                        onClick={() => setKeywordsOpen((v) => !v)}
                    >
                        <Icons.SmallIcon Icon={keywordsOpen ? Icons.Minus : Icons.Plus} />
                        <h5>
                            Keywords
                            {selectedKeywords.length > 0 && (
                                <span> ({selectedKeywords.length} selected)</span>
                            )}
                        </h5>
                    </div>
                    {uploadedFile ? (
                        <button
                            className="flex items-center gap-1 border border-white-300 rounded-full pl-2 pr-2 py-1.5 text-white-500 hover:text-white hover:border-white-400 group/file"
                            onClick={() => setUploadedFile(null)}
                        >
                            <Icons.SmallIcon className="text-white" Icon={Icons.Draft} />
                            <small className="max-w-[180px] truncate">{uploadedFile.name}</small>
                            <Icons.ExtraSmallIcon
                                Icon={Icons.XClose}
                                className="group-hover/file:text-red-200"
                            />
                        </button>
                    ) : (
                        <ButtonTertiary
                            title="Upload PDF"
                            LeftIcon={Icons.Upload}
                            onClick={() => fileInputRef.current?.click()}
                        />
                    )}
                    <input
                        type="file"
                        ref={fileInputRef}
                        onChange={handleFileChange}
                        accept="application/pdf"
                        className="hidden"
                    />
                </div>
                <ExpandableContent isOpen={keywordsOpen}>
                    <div className="pb-4">
                        {KEYWORD_CATEGORIES.map(({ label, options }) => {
                            const isOpen = openCategories.has(label)
                            const selectedCount = selectedKeywords.filter(
                                (k) => k.type === label
                            ).length
                            const allSelected = selectedCount === options.length
                            return (
                                <div key={label}>
                                    <div
                                        className={classNames(
                                            "px-6 py-3 flex items-center gap-3 cursor-pointer transition-colors group",
                                            {
                                                "text-white": isOpen || selectedCount > 0,
                                                "text-gray-300 hover:text-white":
                                                    !isOpen && selectedCount === 0,
                                            }
                                        )}
                                        onClick={() => toggleCategory(label)}
                                    >
                                        <Icons.ExtraSmallIcon
                                            Icon={
                                                isOpen
                                                    ? Icons.ArrowDropdown
                                                    : Icons.ArrowDropdownRight
                                            }
                                        />
                                        <h5>
                                            {label}
                                            {selectedCount > 0 && (
                                                <span> ({selectedCount} selected)</span>
                                            )}
                                        </h5>
                                        <div className="grow" />
                                        <div
                                            className={classNames("transition-opacity", {
                                                "opacity-0 group-hover:opacity-100": !isOpen,
                                            })}
                                        >
                                            <IconButton
                                                onClick={(e) => {
                                                    e.stopPropagation()
                                                    allSelected
                                                        ? deselectAllInCategory(label)
                                                        : selectAllInCategory(label, options)
                                                }}
                                            >
                                                <small className="text-gray-300">
                                                    {allSelected ? "Deselect all" : "Select all"}
                                                </small>
                                            </IconButton>
                                        </div>
                                    </div>
                                    <ExpandableContent isOpen={isOpen}>
                                        <div className="px-6 pb-3 flex flex-wrap gap-2">
                                            {options.map((option) => (
                                                <Status
                                                    as="button"
                                                    key={option}
                                                    type="neutral"
                                                    data-active={selectedKeywords.some(
                                                        (k) =>
                                                            k.type === label && k.value === option
                                                    )}
                                                    variant="outline"
                                                    className="cursor-pointer data-[active=true]:border-blue-500 data-[active=true]:bg-blue-900 data-[active=false]:hover:border-white select-none"
                                                    onClick={() => toggleKeyword(label, option)}
                                                >
                                                    {option}
                                                </Status>
                                            ))}
                                        </div>
                                    </ExpandableContent>
                                </div>
                            )
                        })}
                    </div>
                </ExpandableContent>
            </div>
            <ButtonTertiary
                title="Generate Checklist"
                RightIcon={Icons.ArrowRight}
                disabled={!prompt && !uploadedFile}
                onClick={handleGenerate}
            />
            <div className="h-20" />
        </>
    )
}
